import { httpApi } from '@app/api/http.api'

import config from '@/config'

export type GetMQTTResponseHistory = Omit<GetMQTTResponse, 'device' | 'device_Id'> & {
  maxValue?: number
  sumValue?: number
  indexValue?: number
  day?: string
}

export type GetMQTTResponse = {
  type: string
  value: string
  createdAt: Date
}

export type GetHistoryChartRequest = {
  id: string
  type: string
  fromDate: string
  toDate: string
  indexes: string | number | undefined
}

export type GetTodayChartRequest = {
  id: string
  type: string
  indexes?: string
}

export type ChartDataResponse = {
  value: number
  createdAt: Date
}

export type HistoryChartResponse = {
  id: string
  type: string
  value: number
  createdAt: Date
}

export type GetMQTTPayload = {
  id: string
  selectedTemplateId: string
}

export type DeviceSseData = {
  deviceId: string
  frames: GetMQTTResponse[]
}

export type DeviceSseHandlers = {
  onOpen: () => void
  onData: (data: DeviceSseData) => void
  onHeartbeat: () => void
  onError: () => void
  onParseError: (rawData: string) => void
}

export type DeviceSseConnection = {
  close: () => void
}

const MQTT = 'mqtt'

const asMqttFrame = (value: unknown): GetMQTTResponse | null => {
  if (!value || typeof value !== 'object') return null

  const frame = value as { type?: unknown; value?: unknown; createdAt?: unknown }
  if (typeof frame.type !== 'string' || (typeof frame.value !== 'string' && typeof frame.value !== 'number')) {
    return null
  }

  const createdAt = new Date(frame.createdAt as string | number | Date)
  if (Number.isNaN(createdAt.getTime())) return null

  return { type: frame.type, value: String(frame.value), createdAt }
}

export const connectDeviceSse = (
  deviceIds: string[],
  { onOpen, onData, onHeartbeat, onError, onParseError }: DeviceSseHandlers,
): DeviceSseConnection => {
  const idParam = deviceIds.map(encodeURIComponent).join(',')
  const eventSource = new EventSource(`${config.apiURL}/${MQTT}/sse/${idParam}`, { withCredentials: true })

  eventSource.onopen = onOpen
  eventSource.onmessage = (event) => {
    try {
      const parsed: unknown = JSON.parse(event.data)
      if (!parsed || typeof parsed !== 'object') throw new Error('Invalid SSE payload')

      const envelope = parsed as Record<string, unknown>
      if (envelope.type === 'heartbeat') {
        onHeartbeat()
        return
      }

      if (typeof envelope.deviceId !== 'string') throw new Error('Missing deviceId in SSE payload')

      const frameCandidates = Array.isArray(envelope.frames)
        ? envelope.frames
        : Object.entries(envelope).flatMap(([key, value]) => (/^\d+$/.test(key) ? [value] : []))
      const frames = frameCandidates.flatMap((value) => {
        const frame = asMqttFrame(value)
        return frame ? [frame] : []
      })
      if (frames.length === 0) return

      onData({ deviceId: envelope.deviceId, frames })
    } catch {
      onParseError(event.data)
    }
  }
  eventSource.addEventListener('heartbeat', onHeartbeat)
  eventSource.onerror = onError

  let closed = false
  return {
    close: () => {
      if (closed) return
      closed = true
      eventSource.close()
    },
  }
}

export const getMqtt = (getMqttPayload: GetMQTTPayload): Promise<GetMQTTResponse[]> =>
  httpApi.post<unknown[]>(`${MQTT}/data`, { ...getMqttPayload }).then(({ data }) =>
    data.flatMap((value) => {
      const frame = asMqttFrame(value)
      return frame ? [frame] : []
    }),
  )

export const getTodayChartData = (mqttTypePayload: GetTodayChartRequest): Promise<ChartDataResponse[]> =>
  httpApi.post<ChartDataResponse[]>(`${MQTT}/history/today`, { ...mqttTypePayload }).then(({ data }) => data)

export const getHistoryChartDataDailyHighest = (
  mqttTypePayload: GetHistoryChartRequest,
): Promise<ChartDataResponse[]> =>
  httpApi
    .post<ChartDataResponse[]>(`${MQTT}/history-chart-data-daily-highest`, { ...mqttTypePayload })
    .then(({ data }) => data)

export const getHistoryChartLastDataDaily = (mqttTypePayload: GetHistoryChartRequest): Promise<ChartDataResponse[]> =>
  httpApi
    .post<ChartDataResponse[]>(`${MQTT}/history-chart-last-data-daily`, { ...mqttTypePayload })
    .then(({ data }) => data)

export const getRawMeasurementsForCSV = (mqttTypePayload: GetHistoryChartRequest): Promise<ChartDataResponse[]> =>
  httpApi.post<ChartDataResponse[]>(`${MQTT}/raw-measurements-csv`, { ...mqttTypePayload }).then(({ data }) => data)
